import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Highlight, themes } from 'prism-react-renderer';
import { MessageMetadata } from '../services/aiChatService';
// 移除钉钉认证依赖

interface ThinkingMarkdownRendererProps {
    content: string;
    metadata?: MessageMetadata | null;
    className?: string;
}

const ThinkingMarkdownRenderer: React.FC<ThinkingMarkdownRendererProps> = ({ content, metadata, className = '' }) => {
    const [showThinking, setShowThinking] = useState(true); // 默认显示思考过程
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [audioLoading, setAudioLoading] = useState(false);
    const [audioError, setAudioError] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement>(null);

    // 当收到 </think> 标签时立即折叠思考过程
    useEffect(() => {
        if (content && content.includes('</think>')) {
            // 思考过程结束后立即折叠
            setShowThinking(false);
        }
    }, [content]);

    // 渲染图片消息
    const renderImageMessage = () => {
        if (!metadata?.image_metadata?.url) return null;

        const imageUrl = metadata.image_metadata.url;
        const description = metadata.image_metadata.description || metadata.image_metadata.alt_text || '[图片]';
        const fileSize = metadata.image_metadata.size;

        return (
            <div className="space-y-2 mb-3">
                <div className="relative group">
                    {/* 图片容器 */}
                    <div className="relative overflow-hidden rounded-xl bg-gray-100 shadow-sm border border-gray-200">
                        <img
                            src={imageUrl}
                            alt={description}
                            className="max-w-full h-auto cursor-pointer transition-transform duration-200 group-hover:scale-105"
                            style={{ maxHeight: '400px', maxWidth: '300px' }}
                            onClick={() => {
                                // 点击图片可以放大查看
                                const img = new Image();
                                img.src = imageUrl;
                                const w = window.open('');
                                w?.document.write(`
                                    <html>
                                        <head><title>图片预览</title></head>
                                        <body style="margin:0;padding:20px;background:#000;display:flex;justify-content:center;align-items:center;min-height:100vh;">
                                            <img src="${imageUrl}" style="max-width:100%;max-height:100%;object-fit:contain;" />
                                        </body>
                                    </html>
                                `);
                            }}
                        />

                        {/* 悬停时显示的放大图标 */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* 图片信息 */}
                    <div className="mt-2 text-xs text-gray-500 flex items-center justify-between">
                        <span className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            图片
                        </span>
                        {fileSize && (
                            <span>{(fileSize / 1024).toFixed(1)} KB</span>
                        )}
                    </div>

                    {/* 显示额外的文本内容（如果有） */}
                    {content !== '[图片]' && content !== description && content.trim() && (
                        <p className="text-sm mt-2 text-gray-700 bg-gray-50 p-2 rounded-lg">{content}</p>
                    )}
                </div>
            </div>
        );
    };

    // 语音播放控制函数
    const togglePlay = async () => {
        if (!audioRef.current) {
            console.error('音频元素未找到');
            return;
        }

        // 如果有错误，尝试重新加载
        if (audioError) {
            console.log('重新加载音频...');
            setAudioError(null);
            setAudioLoading(true);
            audioRef.current.load();
            return;
        }

        try {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                console.log('尝试播放音频:', audioRef.current.src);
                await audioRef.current.play();
            }
        } catch (error) {
            console.error('音频播放失败:', error);
            console.log('音频URL:', audioRef.current.src);
            console.log('音频readyState:', audioRef.current.readyState);
            console.log('音频networkState:', audioRef.current.networkState);

            setAudioError('播放失败，点击重试');
        }
    };

    // 格式化时间显示
    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // 渲染语音消息
    const renderVoiceMessage = () => {
        if (!metadata?.voice_metadata) return null;

        const voiceUrl = metadata.voice_metadata.url;
        const transcription = metadata.voice_metadata.transcription?.text;
        const voiceDuration = metadata.voice_metadata.duration || 0;

        return (
            <div className="space-y-2 mb-3">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 shadow-sm max-w-sm hover:shadow-md transition-shadow duration-200">
                    {/* 隐藏的音频元素 */}
                    {voiceUrl && (
                        <audio
                            ref={audioRef}
                            src={voiceUrl}
                            preload="metadata"
                            onLoadStart={() => {
                                console.log('开始加载音频:', voiceUrl);
                                setAudioLoading(true);
                                setAudioError(null);
                            }}
                            onLoadedMetadata={() => {
                                console.log('音频元数据加载完成');
                                if (audioRef.current) {
                                    setDuration(audioRef.current.duration);
                                    console.log('音频时长:', audioRef.current.duration);
                                }
                                setAudioLoading(false);
                            }}
                            onCanPlay={() => {
                                console.log('音频可以播放');
                                setAudioLoading(false);
                            }}
                            onTimeUpdate={() => {
                                if (audioRef.current) {
                                    setCurrentTime(audioRef.current.currentTime);
                                }
                            }}
                            onPlay={() => {
                                console.log('音频开始播放');
                                setIsPlaying(true);
                            }}
                            onPause={() => {
                                console.log('音频暂停');
                                setIsPlaying(false);
                            }}
                            onEnded={() => {
                                console.log('音频播放结束');
                                setIsPlaying(false);
                                setCurrentTime(0);
                            }}
                            onError={(e) => {
                                console.error('音频加载错误:', e);
                                const error = audioRef.current?.error;
                                let errorMessage = '音频加载失败';
                                if (error) {
                                    switch (error.code) {
                                        case error.MEDIA_ERR_ABORTED:
                                            errorMessage = '音频加载被中止';
                                            break;
                                        case error.MEDIA_ERR_NETWORK:
                                            errorMessage = '网络错误';
                                            break;
                                        case error.MEDIA_ERR_DECODE:
                                            errorMessage = '音频解码错误';
                                            break;
                                        case error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                                            errorMessage = '音频格式不支持';
                                            break;
                                    }
                                }
                                setAudioError(errorMessage);
                                setAudioLoading(false);
                                setIsPlaying(false);
                            }}
                        />
                    )}

                    {/* 自定义播放器界面 */}
                    <div className="flex items-center space-x-3">
                        {/* 播放/暂停按钮 */}
                        <button
                            onClick={togglePlay}
                            disabled={!voiceUrl || audioLoading}
                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 ${audioError
                                ? 'bg-red-500 hover:bg-red-600'
                                : audioLoading
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300'
                            }`}
                            title={audioError || (audioLoading ? '加载中...' : isPlaying ? '暂停' : '播放')}
                        >
                            {audioLoading ? (
                                // 加载图标
                                <svg className="w-5 h-5 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                            ) : audioError ? (
                                // 错误图标
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            ) : isPlaying ? (
                                // 暂停图标
                                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                                </svg>
                            ) : (
                                // 播放图标
                                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            )}
                        </button>

                        {/* 语音信息 */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-1">
                                <svg className={`w-4 h-4 text-blue-500 flex-shrink-0 ${isPlaying ? 'animate-pulse' : ''}`} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                                </svg>
                                <span className="text-sm font-medium text-gray-800">语音消息</span>
                                {isPlaying && (
                                    <div className="flex space-x-0.5">
                                        <div className="w-1 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                                        <div className="w-1 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
                                        <div className="w-1 h-4 bg-blue-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                    </div>
                                )}
                            </div>

                            {/* 进度条 */}
                            <div className="w-full bg-blue-200 rounded-full h-1.5 mb-1">
                                <div
                                    className="bg-blue-500 h-1.5 rounded-full transition-all duration-100"
                                    style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
                                />
                            </div>

                            {/* 时间显示或错误信息 */}
                            <div className="flex justify-between text-xs">
                                {audioError ? (
                                    <span className="text-red-500 flex-1">{audioError}</span>
                                ) : (
                                    <>
                                        <span className="text-gray-500">{formatTime(currentTime)}</span>
                                        <span className="text-gray-500">{formatTime(duration || voiceDuration)}</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 转录文本 */}
                    {transcription && transcription.trim() && (
                        <div className="mt-4 bg-white border border-blue-100 rounded-lg p-3">
                            <div className="flex items-center space-x-1 mb-2">
                                <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-xs font-medium text-blue-600">转录文本</span>
                            </div>
                            <div className="text-sm text-gray-700 leading-relaxed">
                                {transcription}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 渲染位置消息
    const renderLocationMessage = () => {
        if (!metadata?.location_metadata) return null;

        const { latitude, longitude, address, poi_name } = metadata.location_metadata;

        return (
            <div className="space-y-2 mb-3">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 shadow-sm">
                    {/* 位置消息头部 */}
                    <div className="flex items-center space-x-2 mb-3">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                        </div>
                        <div>
                            <div className="text-sm font-medium text-gray-800">位置信息</div>
                            <div className="text-xs text-gray-500">地理坐标</div>
                        </div>
                    </div>

                    {/* 地址信息 */}
                    {(address || poi_name) && (
                        <div className="bg-white border border-green-100 rounded-lg p-3 mb-3">
                            {poi_name && (
                                <div className="text-sm font-medium text-gray-800 mb-1">{poi_name}</div>
                            )}
                            {address && (
                                <div className="text-sm text-gray-600">{address}</div>
                            )}
                        </div>
                    )}

                    {/* 坐标信息 */}
                    <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                        <div className="flex items-center justify-between">
                            <div className="text-xs text-gray-500">坐标</div>
                            <button
                                onClick={() => {
                                    // 移除地图功能，仅显示坐标信息
                                    console.log('位置信息:', { latitude, longitude, poi_name, address });
                                }}
                                className="text-xs text-green-600 hover:text-green-700 underline transition-colors"
                            >
                                在地图中查看
                            </button>
                        </div>
                        <div className="text-sm font-mono text-gray-700 mt-1">
                            {latitude?.toFixed(6)}, {longitude?.toFixed(6)}
                        </div>
                    </div>

                    {/* 显示额外的文本内容（如果有） */}
                    {content && content !== address && content !== poi_name && content.trim() && (
                        <div className="mt-3 text-sm text-gray-600 bg-green-50 p-2 rounded-lg border border-green-100">
                            {content}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // 解析思考内容和普通内容
    const parseContent = (text: string) => {
        const parts: Array<{ type: 'thinking' | 'normal', content: string }> = [];
        let currentIndex = 0;

        while (currentIndex < text.length) {
            const thinkStart = text.indexOf('<think>', currentIndex);

            if (thinkStart === -1) {
                // 没有更多思考标签，剩余内容都是普通内容
                const remaining = text.slice(currentIndex);
                if (remaining.trim()) {
                    parts.push({ type: 'normal', content: remaining });
                }
                break;
            }

            // 添加思考标签前的普通内容
            if (thinkStart > currentIndex) {
                const normalContent = text.slice(currentIndex, thinkStart);
                if (normalContent.trim()) {
                    parts.push({ type: 'normal', content: normalContent });
                }
            }

            // 查找思考标签的结束位置
            const thinkEnd = text.indexOf('</think>', thinkStart);
            if (thinkEnd === -1) {
                // 没有找到结束标签，将剩余内容作为思考内容
                const thinkingContent = text.slice(thinkStart + 7); // 跳过 '<think>'
                if (thinkingContent.trim()) {
                    parts.push({ type: 'thinking', content: thinkingContent });
                }
                break;
            }

            // 提取思考内容（不包括标签）
            const thinkingContent = text.slice(thinkStart + 7, thinkEnd);
            if (thinkingContent.trim()) {
                parts.push({ type: 'thinking', content: thinkingContent });
            }

            currentIndex = thinkEnd + 8; // 跳过 '</think>'
        }

        return parts;
    };

    const parts = parseContent(content);
    const hasThinking = parts.some(part => part.type === 'thinking');
    const normalParts = parts.filter(part => part.type === 'normal');
    const thinkingParts = parts.filter(part => part.type === 'thinking');

    // 如果没有内容，不显示任何内容
    if (!content || content.trim() === '') {
        return null;
    }

    return (
        <div className={`markdown-content ${className}`}>
            {/* 特殊消息类型渲染 */}
            {metadata?.message_type === 'image' && renderImageMessage()}
            {metadata?.message_type === 'voice' && renderVoiceMessage()}
            {metadata?.message_type === 'location' && renderLocationMessage()}

            {/* 思考内容 - 默认显示，可折叠 */}
            {hasThinking && (
                <div className="mb-3">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                        <button
                            onClick={() => setShowThinking(!showThinking)}
                            className="w-full px-3 py-2 text-left flex items-center justify-between text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                            <div className="flex items-center space-x-2">
                                <span className="text-gray-500">🧠</span>
                                <span className="text-xs font-medium">AI 思考过程</span>
                            </div>
                            <svg
                                className={`w-4 h-4 transition-transform ${showThinking ? 'rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {showThinking && (
                            <div className="px-3 pb-3 border-t border-gray-200">
                                {thinkingParts.map((part, index) => (
                                    <div key={`thinking-${index}`} className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap mt-2">
                                        {part.content}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* 普通内容 */}
            {normalParts.map((part, index) => (
                <ReactMarkdown
                    key={`normal-${index}`}
                    remarkPlugins={[remarkGfm]}
                    components={{
                        // 自定义标题组件
                        h1: ({ children }) => (
                            <h1 className="text-base font-bold text-gray-900 mb-3 mt-4 first:mt-0 pb-2 border-b border-gray-200">
                                {children}
                            </h1>
                        ),
                        h2: ({ children }) => (
                            <h2 className="text-sm font-semibold text-gray-900 mb-2 mt-3 first:mt-0">
                                {children}
                            </h2>
                        ),
                        h3: ({ children }) => (
                            <h3 className="text-sm font-medium text-gray-900 mb-1 mt-2 first:mt-0">
                                {children}
                            </h3>
                        ),

                        // 自定义段落组件
                        p: ({ children }) => {
                            // 处理文本内容，强制换行
                            const processText = (text: any): any => {
                                if (typeof text !== 'string') return text;

                                // 更激进的换行处理
                                let processed = text
                                    // 在所有 " - " 前添加换行
                                    .replace(/\s+-\s+/g, '\n- ')
                                    // 在冒号后面如果跟着内容和减号，就换行
                                    .replace(/:\s*([^:\n-]+?)\s*-\s*/g, ': $1\n- ')
                                    // 在数字开头的条目前添加双换行
                                    .replace(/(\d+\.\s*)/g, '\n\n$1')
                                    // 在重复的字段名称前换行（如 订单号、客户名称 等）
                                    .replace(/(订单号|订单行号|订单类型|物料号|客户牌号|客户色号|产品线|紧急订单|订单数量|单价金额|订单金额|计划交付日期)/g, '\n$1')
                                    // 清理开头多余的换行
                                    .replace(/^\n+/, '')
                                    // 清理连续的换行，保留最多两个
                                    .replace(/\n{3,}/g, '\n\n');



                                return processed.trim();
                            };

                            const processedChildren = React.Children.map(children, (child) => {
                                return typeof child === 'string' ? processText(child) : child;
                            });

                            return (
                                <p className="text-sm text-gray-700 mb-2 leading-relaxed whitespace-pre-wrap break-words">
                                    {processedChildren}
                                </p>
                            );
                        },

                        // 自定义列表组件
                        ul: ({ children }) => (
                            <ul className="list-disc list-inside mb-2 space-y-1 text-sm text-gray-700 pl-1">
                                {children}
                            </ul>
                        ),
                        ol: ({ children }) => (
                            <ol className="list-decimal list-inside mb-2 space-y-1 text-sm text-gray-700 pl-1">
                                {children}
                            </ol>
                        ),
                        li: ({ children }) => (
                            <li className="ml-1 leading-relaxed">
                                {children}
                            </li>
                        ),

                        // 自定义代码块组件
                        code: ({ className, children, ...props }: any) => {
                            const match = /language-(\w+)/.exec(className || '');
                            const isInline = !className || !className.startsWith('language-');
                            const code = String(children).replace(/\n$/, '');
                            if (isInline) {
                                return (
                                    <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-xs font-mono" {...props}>
                                        {children}
                                    </code>
                                );
                            }
                            const language = (match && match[1]) ? match[1] : 'javascript';
                            return (
                                <div className="relative mb-2">
                                    <Highlight theme={themes.vsLight} code={code} language={language as any}>
                                        {({ className: cls, style, tokens, getLineProps, getTokenProps }: any) => (
                                            <pre className={`${cls} bg-gray-50 rounded p-2 overflow-x-auto text-xs border`} style={style}>
                                                {tokens.map((line: any, i: number) => (
                                                    <div key={i} {...getLineProps({ line, key: i })}>
                                                        {line.map((token: any, key: number) => (
                                                            <span key={key} {...getTokenProps({ token, key })} />
                                                        ))}
                                                    </div>
                                                ))}
                                            </pre>
                                        )}
                                    </Highlight>
                                    {match && (
                                        <span className="absolute top-1 right-1 text-xs text-gray-500 bg-white px-1 py-0.5 rounded">
                                            {match[1]}
                                        </span>
                                    )}
                                </div>
                            );
                        },

                        // 自定义引用块组件
                        blockquote: ({ children }) => (
                            <blockquote className="border-l-2 border-blue-200 pl-2 py-1 mb-2 bg-blue-50 rounded-r">
                                <div className="text-sm text-gray-700 italic">
                                    {children}
                                </div>
                            </blockquote>
                        ),

                        // 自定义链接组件
                        a: ({ href, children }) => (
                            <a
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 underline text-sm"
                            >
                                {children}
                            </a>
                        ),

                        // 自定义表格组件
                        table: ({ children }) => (
                            <div className="overflow-x-auto mb-4 rounded-lg border border-gray-200 shadow-sm">
                                <table className="min-w-full table-auto text-sm">
                                    {children}
                                </table>
                            </div>
                        ),
                        thead: ({ children }) => (
                            <thead className="bg-gray-100">
                            {children}
                            </thead>
                        ),
                        th: ({ children }) => (
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                                {children}
                            </th>
                        ),
                        td: ({ children }) => (
                            <td className="px-3 py-2 text-xs text-gray-700 border-b border-gray-100 max-w-xs break-words leading-relaxed">
                                {children}
                            </td>
                        ),

                        // 自定义强调组件
                        strong: ({ children }) => (
                            <strong className="font-semibold text-gray-900">
                                {children}
                            </strong>
                        ),
                        em: ({ children }) => (
                            <em className="italic text-gray-700">
                                {children}
                            </em>
                        ),

                        // 自定义分割线组件
                        hr: () => (
                            <hr className="my-4 border-gray-200" />
                        ),
                    }}
                >
                    {part.content}
                </ReactMarkdown>
            ))}


        </div>
    );
};

export default ThinkingMarkdownRenderer;
